import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  ExternalLink,
  ArrowLeft,
  AlertCircle,
  Wrench,
  TrendingUp,
  Check,
} from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { SITE } from "@/lib/seo";
import { CASES, getCaseBySlug, type CaseStudy } from "@/data/cases";
import NotFound from "./NotFound";
import { cn } from "@/lib/utils";

const buildBreadcrumb = (study: CaseStudy) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE.url}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Casos de éxito",
      item: `${SITE.url}/casos-de-exito`,
    },
    {
      "@type": "ListItem",
      position: 3,
      name: study.name,
      item: `${SITE.url}/casos/${study.slug}`,
    },
  ],
});

const buildArticle = (study: CaseStudy) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: `${study.name}: ${study.headline}`,
  description: study.metaDescription,
  about: study.industry,
  author: { "@type": "Organization", name: "Vibra Marketing" },
  publisher: {
    "@type": "Organization",
    name: "Vibra Marketing",
    logo: { "@type": "ImageObject", url: `${SITE.url}/favicon.ico` },
  },
  mainEntityOfPage: `${SITE.url}/casos/${study.slug}`,
});

const StepBadge = ({ n, label }: { n: string; label: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="font-display text-2xl text-gradient">{n}</span>
    <span className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
    <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </span>
  </div>
);

const CasoDetalle = () => {
  const { slug } = useParams<{ slug: string }>();
  const study = slug ? getCaseBySlug(slug) : undefined;

  if (!study) return <NotFound />;

  const URL = `${SITE.url}/casos/${study.slug}`;
  const related = CASES.filter((c) => c.slug !== study.slug).slice(0, 3);
  const bullets =
    study.whatWeDidBullets && study.whatWeDidBullets.length > 0
      ? study.whatWeDidBullets
      : study.whatWeDid.split(/[.,]\s+/).filter((s) => s.length > 8).slice(0, 5);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{study.title}</title>
        <meta name="description" content={study.metaDescription} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={study.title} />
        <meta property="og:description" content={study.metaDescription} />
        <meta property="og:url" content={URL} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(buildBreadcrumb(study))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(buildArticle(study))}
        </script>
      </Helmet>

      <Header />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="container px-6 pt-8 text-xs text-muted-foreground"
        >
          <ol className="flex items-center gap-2 flex-wrap">
            <li>
              <Link to="/" className="hover:text-foreground">Inicio</Link>
            </li>
            <li aria-hidden>/</li>
            <li>
              <Link to="/casos-de-exito" className="hover:text-foreground">
                Casos de éxito
              </Link>
            </li>
            <li aria-hidden>/</li>
            <li className="text-foreground">{study.name}</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container px-6 pt-10 pb-12 max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div
              className={cn(
                "w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0 shadow-md",
                study.logoBg
              )}
            >
              <img
                src={study.logo}
                alt={study.name}
                className={cn(
                  "w-10 h-10 object-contain",
                  study.logoRounded && "rounded-full"
                )}
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {study.industry} · {study.period}
              </p>
              {study.url !== "#" && (
                <a
                  href={study.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Visitar sitio <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>

          <h1 className="font-display text-4xl md:text-6xl uppercase leading-[1.05] mb-6">
            {study.name} <br className="hidden sm:block" />
            <span className="text-gradient">{study.headline}</span>
          </h1>

          {/* Datos rápidos: stat cards grandes */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
            {study.stats.slice(0, 4).map((s) => (
              <div
                key={s.label}
                className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-purple-500/5 to-transparent p-5 text-center shadow-[0_0_40px_-22px_hsl(var(--primary)/0.5)]"
              >
                <div className="font-display text-3xl md:text-4xl font-black text-gradient leading-none mb-2">
                  {s.value}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </header>

        {/* Timeline 01 → 02 → 03 */}
        <article className="container px-6 max-w-4xl mx-auto pb-20 space-y-14">
          {/* 01 Punto de partida */}
          <section>
            <StepBadge n="01" label="Punto de partida" />
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8 flex gap-4">
              <div className="w-11 h-11 rounded-xl bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5" />
              </div>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed flex-1">
                {study.startingPoint}
              </p>
            </div>
          </section>

          {/* 02 Qué hicimos */}
          <section>
            <StepBadge n="02" label="Qué hicimos" />
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Wrench className="w-5 h-5" />
                </div>
                <h2 className="font-display text-2xl uppercase">Acciones</h2>
              </div>

              <ul className="space-y-3 mb-8">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 mt-0.5 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm md:text-base text-foreground/90 leading-relaxed">
                      {b}
                    </span>
                  </li>
                ))}
              </ul>

              {study.services.length > 0 && (
                <div className="pt-6 border-t border-border">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
                    Servicios utilizados
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {study.services.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/servicios/${s.slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-primary/30 bg-accent text-xs font-medium hover:bg-primary/10 hover:border-primary/50 transition-colors"
                      >
                        {s.label}
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 03 Resultados */}
          <section>
            <StepBadge n="03" label="Resultados" />
            <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-purple-500/5 to-transparent p-6 md:p-8 shadow-[0_0_60px_-25px_hsl(var(--primary)/0.4)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <h2 className="font-display text-2xl uppercase">Cifras</h2>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {study.stats.map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl border border-border bg-background/70 p-5 text-center"
                  >
                    <div className="font-display text-3xl md:text-4xl font-black text-gradient leading-none mb-2">
                      {s.value}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
              {study.resultsHighlight && (
                <p className="text-center text-base md:text-lg font-medium text-foreground">
                  “{study.resultsHighlight}”
                </p>
              )}
            </div>
          </section>
        </article>

        {/* CTA */}
        <section className="container px-6 pb-16">
          <div className="rounded-3xl bg-gradient-to-br from-primary/10 via-purple-500/5 to-background border border-primary/20 px-6 py-12 md:py-14 text-center shadow-[0_0_60px_-20px_hsl(var(--primary)/0.3)]">
            <h2 className="font-display text-2xl md:text-4xl uppercase mb-3">
              ¿Tienes un negocio similar?
            </h2>
            <p className="text-muted-foreground mb-7 max-w-xl mx-auto">
              Agenda una llamada gratuita y te contamos cómo conseguir resultados
              parecidos.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
            >
              Agendar llamada gratuita <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Related */}
        <section className="container px-6 pb-24">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-xl md:text-2xl uppercase">
              Otros casos
            </h2>
            <Link
              to="/casos-de-exito"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Ver todos
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/casos/${r.slug}`}
                className="group rounded-2xl border border-border bg-card p-5 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                  {r.industry}
                </p>
                <h3 className="font-semibold text-sm mb-2">{r.name}</h3>
                <p className="text-lg font-black text-gradient leading-tight">
                  {r.headline}
                </p>
                <span className="mt-4 inline-flex items-center gap-1 text-[11px] text-primary group-hover:gap-2 transition-all">
                  Ver caso <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CasoDetalle;
